import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Check, CheckCheck, Clock, AlertCircle, ChevronDown, FileText, Paperclip, Download, Loader2, X, MoreVertical, ExternalLink, Maximize2, Play, Pause, Mic } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { locale } from "@/lib/format";

/* Player vocale/audio compatto: play, barra, durata. */
function AudioPlayer({ url, voice }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [dur, setDur] = useState(0);
  const [pos, setPos] = useState(0);
  const [rate, setRate] = useState(1);

  const cycleRate = () => {
    const next = rate === 1 ? 1.5 : rate === 1.5 ? 2 : 1;
    setRate(next);
    if (ref.current) ref.current.playbackRate = next;
  };

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) { el.play(); setPlaying(true); } else { el.pause(); setPlaying(false); }
  };

  return (
    <div className="flex items-center gap-2 min-w-[190px] py-0.5" data-testid="wa-audio-player">
      <audio
        ref={ref} src={url} preload="metadata"
        onLoadedMetadata={(e) => setDur(Number.isFinite(e.target.duration) ? e.target.duration : 0)}
        onTimeUpdate={(e) => setPos(e.target.currentTime)}
        onEnded={() => { setPlaying(false); setPos(0); }}
      />
      <button onClick={toggle} data-testid="wa-audio-toggle"
        aria-label={playing ? t("chat.pause") : t("chat.play")}
        title={playing ? t("chat.pause") : t("chat.play")}
        className="h-8 w-8 flex-shrink-0 rounded-full bg-black/10 hover:bg-black/15 flex items-center justify-center">
        {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <input
          type="range" min={0} max={dur || 0} step="0.1" value={Math.min(pos, dur || 0)}
          onChange={(e) => { const v = Number(e.target.value); setPos(v); if (ref.current) ref.current.currentTime = v; }}
          aria-label={t("chat.audioProgress")}
          className="w-full h-1 accent-emerald-700 cursor-pointer"
        />
        <div className="flex items-center gap-1 text-[10px] opacity-70 tabular-nums">
          {voice && <Mic className="h-2.5 w-2.5" />}
          {mmss(playing || pos ? pos : dur)}{dur ? ` / ${mmss(dur)}` : ""}
        </div>
      </div>
      <button onClick={cycleRate} data-testid="wa-audio-rate"
        aria-label={t("chat.playbackSpeed", { rate })}
        title={t("chat.changeSpeed")}
        className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-black/10 hover:bg-black/15 tabular-nums">
        {rate}x
      </button>
    </div>
  );
}

/* Visualizzatore a schermo intero (immagini e video) sulla stessa pagina. */
function Lightbox({ url, kind, filename, onClose, onDownload }) {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(false);
  const [menu, setMenu] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center animate-in fade-in duration-150"
      onClick={onClose}
      data-testid="wa-lightbox"
    >
      <div className="absolute top-3 right-3 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <div className="relative">
          <button onClick={() => setMenu((v) => !v)} title={t("chat.moreOptions")}
            data-testid="wa-lightbox-menu"
            className="p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10">
            <MoreVertical className="h-5 w-5" />
          </button>
          {menu && (
            <div className="absolute right-0 mt-1 w-44 rounded-xl bg-white shadow-xl py-1 text-sm">
              <button onClick={() => { setMenu(false); onDownload(); }}
                data-testid="wa-lightbox-download"
                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2">
                <Download className="h-4 w-4" /> {t("common.download")}
              </button>
              <button onClick={() => { setMenu(false); window.open(url, "_blank"); }}
                data-testid="wa-lightbox-newtab"
                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2">
                <ExternalLink className="h-4 w-4" /> {t("common.openInNewTab")}
              </button>
            </div>
          )}
        </div>
        <button onClick={onClose} title={t("chat.closeEsc")} data-testid="wa-lightbox-close"
          className="p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10">
          <X className="h-5 w-5" />
        </button>
      </div>
      {filename && (
        <div className="absolute top-4 left-4 text-xs text-white/70 max-w-[50%] truncate">{filename}</div>
      )}
      <div className="max-w-[95vw] max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        {kind === "video" ? (
          <video src={url} controls autoPlay className="max-h-[90vh] max-w-[95vw]" />
        ) : (
          <img
            src={url} alt={filename || t("chat.attachment")}
            onClick={() => setZoom((v) => !v)}
            data-testid="wa-lightbox-image"
            className={zoom ? "max-w-none cursor-zoom-out" : "max-h-[90vh] max-w-[95vw] cursor-zoom-in"}
            style={zoom ? { width: "160vw" } : undefined}
          />
        )}
      </div>
    </div>
  );
}

/* Anteprima allegato: il binario è dietro auth (proxy Meta), quindi niente src diretto. */
function MediaAttachment({ m }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  const [full, setFull] = useState(false);
  const isDoc = m.media_type === "document";
  const isSticker = m.media_type === "sticker";
  const isVideo = m.media_type === "video";
  const isAudio = m.media_type === "audio";

  useEffect(() => {
    if (isDoc) return;
    let revoke;
    let alive = true;
    api.get(`/whatsapp/media/${m.id}`, { responseType: "blob" })
      .then((r) => {
        if (!alive) return;
        revoke = URL.createObjectURL(r.data);
        setUrl(revoke);
      })
      .catch(() => alive && setErr(true));
    return () => { alive = false; if (revoke) URL.revokeObjectURL(revoke); };
  }, [m.id, isDoc]);

  const download = async () => {
    try {
      const r = await api.get(`/whatsapp/media/${m.id}`, { responseType: "blob" });
      const href = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = href; a.download = m.media_filename || "attachment";
      a.click();
      URL.revokeObjectURL(href);
    } catch { setErr(true); }
  };

  if (isDoc || err) {
    return (
      <button onClick={download} data-testid={`wa-media-doc-${m.id}`}
        className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded-lg bg-black/5 hover:bg-black/10 transition-colors">
        <Paperclip className="h-4 w-4 flex-shrink-0" />
        <span className="flex-1 min-w-0 truncate text-xs font-bold">
          {m.media_filename || (err ? t("chat.attachmentExpired") : t("chat.attachment"))}
        </span>
        {!err && <Download className="h-3.5 w-3.5 flex-shrink-0 opacity-60" />}
      </button>
    );
  }
  if (!url) {
    return (
      <div className="flex items-center gap-2 text-xs opacity-60 py-4 justify-center" data-testid={`wa-media-loading-${m.id}`}>
        <Loader2 className="h-4 w-4 animate-spin" /> {t("chat.loadingAttachment")}
      </div>
    );
  }

  const viewer = full && (
    <Lightbox url={url} kind={isVideo ? "video" : "image"} filename={m.media_filename}
              onClose={() => setFull(false)} onDownload={download} />
  );

  if (isAudio) {
    return <AudioPlayer url={url} voice={m.media_voice} />;
  }

  if (isSticker) {
    return (
      <>
        <img src={url} alt={t("chat.sticker")} data-testid={`wa-media-sticker-${m.id}`}
             onClick={() => setFull(true)}
             className="w-[140px] h-[140px] object-contain cursor-zoom-in" />
        {viewer}
      </>
    );
  }
  if (isVideo) {
    return (
      <>
        <div className="relative">
          <video src={url} controls className="rounded-lg max-h-64 w-full"
                 data-testid={`wa-media-video-${m.id}`} />
          <button onClick={() => setFull(true)} title={t("chat.openFullscreen")}
            data-testid={`wa-media-video-expand-${m.id}`}
            className="absolute top-1.5 right-1.5 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70">
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
        </div>
        {viewer}
      </>
    );
  }
  return (
    <>
      <img src={url} alt={m.media_filename || t("chat.attachment")} loading="lazy"
           data-testid={`wa-media-image-${m.id}`}
           className="rounded-lg max-h-64 w-full object-cover cursor-zoom-in"
           onClick={() => setFull(true)} />
      {viewer}
    </>
  );
}

/* Stato outbound: SOLO status realmente persistiti (nessuna simulazione). */
const STATUS_MAP = {
  read: { Icon: CheckCheck, cls: "text-sky-500", label: "chat.status.read" },
  delivered: { Icon: CheckCheck, cls: "text-gray-500", label: "chat.status.delivered" },
  sent: { Icon: Check, cls: "text-gray-500", label: "chat.status.sent" },
  accepted: { Icon: Check, cls: "text-gray-500", label: "chat.status.sent" },
  pending: { Icon: Clock, cls: "text-gray-400", label: "chat.status.pending" },
  queued: { Icon: Clock, cls: "text-gray-400", label: "chat.status.queued" },
  stubbed: { Icon: Clock, cls: "text-gray-400", label: "chat.status.pending" },
  failed: { Icon: AlertCircle, cls: "text-rose-600", label: "chat.status.failed" },
  error: { Icon: AlertCircle, cls: "text-rose-600", label: "chat.status.failed" },
};

export function MessageStatus({ m }) {
  const { t } = useTranslation();
  const key = String(m.last_status || m.status || "").toLowerCase();
  const s = STATUS_MAP[key];
  if (!s) return null;
  const failed = key === "failed" || key === "error";
  const detail = failed
    ? m.last_status_error_title || t("chat.notSent")
    : t(s.label);
  const { Icon } = s;
  return (
    <span
      role="img"
      aria-label={t(s.label)}
      title={detail}
      data-testid={`wa-status-${key}`}
      data-status={key}
      className={`inline-flex items-center ${s.cls}`}
    >
      <Icon className="h-3.5 w-3.5" />
    </span>
  );
}

export function MessageBubble({ m }) {
  const { t } = useTranslation();
  const out = m.direction === "out";
  const key = String(m.last_status || m.status || "").toLowerCase();
  const failed = out && (key === "failed" || key === "error");
  const text = m.rendered_body || m.body || "";
  const isSticker = m.media_type === "sticker" && m.media_id;

  // Sticker: come in WhatsApp, nessuna bubble attorno.
  if (isSticker) {
    return (
      <div className={`flex ${out ? "justify-end" : "justify-start"}`}
           data-testid={`wa-bubble-${m.id}`} data-direction={out ? "out" : "in"}>
        <div className="relative max-w-[75%]">
          <MediaAttachment m={m} />
          <div className="flex items-center justify-end gap-1 text-[10px] text-[color:var(--sp-text-muted)]">
            <span className="tabular-nums">{hhmm(m.timestamp)}</span>
            {out && <MessageStatus m={m} />}
          </div>
          {m.reaction?.emoji && <ReactionPill emoji={m.reaction.emoji} />}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${out ? "justify-end" : "justify-start"}`} data-testid={`wa-bubble-${m.id}`}
         data-direction={out ? "out" : "in"}>
      <div
        className={`relative max-w-[75%] rounded-2xl px-3 py-1.5 text-sm shadow-sm ${
          m.reaction?.emoji ? "mb-3" : ""
        } ${
          out
            ? failed
              ? "bg-rose-50 border border-rose-200 text-gray-900 rounded-br-sm"
              : "bg-[#d9fdd3] text-gray-900 rounded-br-sm"
            : "bg-white border border-[color:var(--sp-border)] text-[color:var(--sp-text)] rounded-bl-sm"
        }`}
      >
        {m.template_name && (
          <div className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider mb-0.5 ${
            out ? "text-emerald-800/60" : "text-[color:var(--sp-text-muted)]"}`}
            data-testid="wa-template-label">
            <FileText className="h-2.5 w-2.5" /> {m.template_name}
          </div>
        )}
        {m.template_header && (
          <div className="font-bold leading-snug break-words">{m.template_header}</div>
        )}
        {m.media_id && (
          <div className="mb-1 -mx-1" data-testid={`wa-media-${m.id}`}>
            <MediaAttachment m={m} />
          </div>
        )}
        <div className="whitespace-pre-wrap break-words leading-relaxed">{text}</div>
        {m.template_footer && (
          <div className={`text-[11px] mt-1 ${out ? "text-emerald-900/50" : "text-[color:var(--sp-text-muted)]"}`}>
            {m.template_footer}
          </div>
        )}
        <div className={`flex items-center justify-end gap-1 text-[10px] mt-0.5 ${
          out ? "text-emerald-900/60" : "text-[color:var(--sp-text-muted)]"}`}>
          <span className="tabular-nums">{hhmm(m.timestamp)}</span>
          {out && <MessageStatus m={m} />}
        </div>
        {failed && (
          <div className="text-[10px] text-rose-700 font-bold" data-testid="wa-failed-note">
            {t("chat.notSent")}
          </div>
        )}
        {m.reaction?.emoji && <ReactionPill emoji={m.reaction.emoji} />}
      </div>
    </div>
  );
}

function ReactionPill({ emoji }) {
  const { t } = useTranslation();
  return (
    <span
      data-testid="wa-reaction"
      title={t("chat.reactedWith", { emoji })}
      aria-label={t("chat.reaction", { emoji })}
      className="absolute -bottom-3 right-2 px-1.5 py-0.5 rounded-full bg-white border border-[color:var(--sp-border)] shadow-sm text-xs leading-none"
    >
      {emoji}
    </span>
  );
}

function DateSeparator({ iso }) {
  const { t } = useTranslation();
  return (
    <div className="flex justify-center my-2">
      <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/80 text-[color:var(--sp-text-muted)] shadow-sm">
        {dayLabel(iso, t)}
      </span>
    </div>
  );
}

/**
 * Lista messaggi con scroll INTERNO, separatori data e auto-scroll intelligente.
 * Il parent deve darle uno spazio definito (flex-1 min-h-0 oppure un'altezza).
 */
export function MessageList({ messages, emptyText,
                             testId = "wa-messages", className = "" }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const atBottom = useRef(true);
  const [showJump, setShowJump] = useState(false);
  const count = messages.length;
  const lastId = count ? messages[count - 1].id : null;

  const scrollToBottom = useCallback((behavior = "auto") => {
    const el = ref.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    atBottom.current = true;
    setShowJump(false);
  }, []);

  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    atBottom.current = near;
    if (near) setShowJump(false);
  };

  useLayoutEffect(() => {
    if (atBottom.current) scrollToBottom();
    else setShowJump(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, lastId]);

  useEffect(() => { atBottom.current = true; }, []);

  let prevDay = null;
  return (
    <div className={`relative flex-1 min-h-0 ${className}`}>
      <div
        ref={ref}
        onScroll={onScroll}
        className="h-full overflow-y-auto px-4 py-3 space-y-1.5 bg-[#efeae2]"
        data-testid={testId}
      >
        {count === 0 ? (
          <div className="h-full flex items-center justify-center text-xs text-[color:var(--sp-text-muted)]">
            {emptyText || t("chat.noMessagesYet")}
          </div>
        ) : messages.map((m) => {
          const day = dayKey(m.timestamp);
          const sep = day !== prevDay;
          prevDay = day;
          return (
            <div key={m.id}>
              {sep && <DateSeparator iso={m.timestamp} />}
              <MessageBubble m={m} />
            </div>
          );
        })}
      </div>
      {showJump && (
        <button
          onClick={() => scrollToBottom("smooth")}
          data-testid="wa-jump-latest"
          className="absolute bottom-3 right-4 inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded-full bg-white shadow-md border border-[color:var(--sp-border)] hover:bg-slate-50"
        >
          <ChevronDown className="h-3.5 w-3.5" /> {t("chat.newMessages")}
        </button>
      )}
    </div>
  );
}

/* Testo di preview per la lista conversazioni: contenuto reale, mai il nome tecnico. */
export function previewText(t) {
  return t.last_message || "…";
}

function hhmm(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function mmss(s) {
  const t = Math.max(0, Math.round(s || 0));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
}

function dayKey(iso) {
  try { return new Date(iso).toDateString(); } catch { return String(iso); }
}

function dayLabel(iso, t) {
  try {
    const d = new Date(iso);
    const today = new Date();
    const yest = new Date(); yest.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return t("common.today");
    if (d.toDateString() === yest.toDateString()) return t("common.yesterday");
    return d.toLocaleDateString(locale(), { day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
}
